import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail, MARK_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Economic Mobility", "Labor", "Civic Engagement", "Housing", "Health", "Report"];
const PRODUCT_CATS = ["Mug", "Pen", "Tote", "T-Shirt", "Merch"];
const emptyArticle = { title: "", category: "Report", excerpt: "", body: "", cover_image: "", author: "The Canary Report", featured: false, published: true };
const emptyStory = { title: "", location: "", description: "", photographer: "The Canary Report", cover_image: "", imagesText: "" };
const emptyEvent = { title: "", date: "", location: "", description: "", image: "", link: "" };
const emptyCorr = { name: "", role: "Correspondent", photo: "", bio: "", substack: "", linkedin: "" };
const emptyProduct = { name: "", category: "Merch", price: "", image: "", description: "", link: "", sort: 0 };
const emptyPotm = { title: "", photo: "", photographer: "", location: "", month: "", intro: "", interviewText: "", featured: true };
const emptySponsor = { name: "", logo: "", link: "", tier: "Sponsor", blurb: "", sort: 0, dark_chip: false, compact: false };
const SPONSOR_TIERS = ["Founding Partner", "Editorial Partner", "Community Partner", "Foundation Backer", "Sponsor"];

const interviewToText = (arr = []) =>
  (arr || [])
    .map((qa) => `Q: ${qa?.question || ""}\nA: ${qa?.answer || ""}`)
    .join("\n\n");

const textToInterview = (text = "") => {
  const blocks = (text || "").split(/\n\s*\n+/);
  const out = [];
  for (const b of blocks) {
    const qm = b.match(/Q\s*:\s*([\s\S]*?)(?=\n\s*A\s*:|$)/i);
    const am = b.match(/A\s*:\s*([\s\S]*)/i);
    const q = qm ? qm[1].trim() : "";
    const a = am ? am[1].trim() : "";
    if (q || a) out.push({ question: q, answer: a });
  }
  return out;
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  try { const d = new Date(iso); const off = d.getTimezoneOffset(); return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16); }
  catch { return ""; }
};

const cellBtn = "rounded-none border-ink";

const Row = ({ img, meta, title, onEdit, onDelete, testid }) => (
  <div data-testid={testid} className="bg-white border border-ink/15 p-4 flex items-center gap-4">
    {img ? <img src={img} alt="" className="h-16 w-24 object-cover bg-paper flex-shrink-0" /> : <div className="h-16 w-24 bg-paper flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="label text-ink/50">{meta}</p>
      <p className="font-display text-lg font-bold truncate">{title}</p>
    </div>
    <div className="flex gap-2">
      <Button size="icon" variant="outline" onClick={onEdit} className={cellBtn}><Pencil size={15} /></Button>
      <Button size="icon" variant="outline" onClick={onDelete} className={`${cellBtn} hover:bg-destructive hover:text-white`}><Trash2 size={15} /></Button>
    </div>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const [data, setData] = useState({ articles: [], stories: [], inquiries: [], events: [], correspondents: [], products: [], messages: [], potm: [], potmSubs: [], sponsors: [] });

  const [dialog, setDialog] = useState(null); // 'article'|'story'|'event'|'corr'|'product'|null
  const [editing, setEditing] = useState(null);
  const [artForm, setArtForm] = useState(emptyArticle);
  const [storyForm, setStoryForm] = useState(emptyStory);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [corrForm, setCorrForm] = useState(emptyCorr);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [potmForm, setPotmForm] = useState(emptyPotm);
  const [sponForm, setSponForm] = useState(emptySponsor);
  const [bulkText, setBulkText] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, s, i, e, c, p, m, pm, ps] = await Promise.all([
        api.get("/admin/articles"), api.get("/photostories"), api.get("/admin/inquiries"),
        api.get("/events"), api.get("/correspondents"), api.get("/products"), api.get("/admin/messages"),
        api.get("/potm"), api.get("/admin/potm-submissions"),
      ]);
      const sp = await api.get("/sponsors");
      setData({ articles: a.data, stories: s.data, inquiries: i.data, events: e.data, correspondents: c.data, products: p.data, messages: m.data, potm: pm.data, potmSubs: ps.data, sponsors: sp.data });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const err = (e2) => toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
  const del = async (path, msg = "Deleted") => { if (!window.confirm("Delete this item?")) return; await api.delete(path); toast.success(msg); load(); };

  // Article
  const saveArt = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...artForm };
      ["id", "slug", "read_time", "created_at"].forEach((k) => delete payload[k]);
      if (editing) await api.put(`/admin/articles/${editing}`, payload); else await api.post("/admin/articles", payload);
      toast.success(editing ? "Article updated" : "Article published"); setDialog(null); load();
    } catch (e2) { err(e2); }
  };
  // Story
  const onStoryImages = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 4 * 1024 * 1024) { toast.error(`${file.name} is over 4MB`); return; }
      const r = new FileReader();
      r.onload = () => setStoryForm((f) => ({ ...f, imagesText: (f.imagesText ? f.imagesText + "\n" : "") + r.result }));
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const saveStory = async (ev) => {
    ev.preventDefault();
    try {
      const images = storyForm.imagesText.split("\n").map((x) => x.trim()).filter(Boolean);
      const payload = { title: storyForm.title, location: storyForm.location, description: storyForm.description, photographer: storyForm.photographer, cover_image: storyForm.cover_image || images[0] || "", images };
      if (editing) await api.put(`/admin/photostories/${editing}`, payload); else await api.post("/admin/photostories", payload);
      toast.success("Saved"); setDialog(null); load();
    } catch (e2) { err(e2); }
  };
  // Event
  const saveEvent = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...eventForm };
      if (payload.date) payload.date = new Date(payload.date).toISOString();
      if (editing) await api.put(`/admin/events/${editing}`, payload); else await api.post("/admin/events", payload);
      toast.success("Saved"); setDialog(null); load();
    } catch (e2) { err(e2); }
  };
  // Correspondent
  const saveCorr = async (ev) => {
    ev.preventDefault();
    try {
      if (editing) await api.put(`/admin/correspondents/${editing}`, corrForm); else await api.post("/admin/correspondents", corrForm);
      toast.success("Saved"); setDialog(null); load();
    } catch (e2) { err(e2); }
  };
  // Product
  const saveProd = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...prodForm, sort: Number(prodForm.sort) || 0 };
      if (editing) await api.put(`/admin/products/${editing}`, payload); else await api.post("/admin/products", payload);
      toast.success("Saved"); setDialog(null); load();
    } catch (e2) { err(e2); }
  };
  // Photo of the Month
  const savePotm = async (ev) => {
    ev.preventDefault();
    try {
      const { interviewText, ...rest } = potmForm;
      const payload = { ...rest, interview: textToInterview(interviewText) };
      if (editing) await api.put(`/admin/potm/${editing}`, payload); else await api.post("/admin/potm", payload);
      toast.success("Saved"); setDialog(null); load();
    } catch (e2) { err(e2); }
  };
  // Sponsor
  const onSponLogo = (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo is over 2MB"); e.target.value = ""; return; }
    const r = new FileReader();
    r.onload = () => setSponForm((f) => ({ ...f, logo: r.result }));
    r.readAsDataURL(file);
    e.target.value = "";
  };
  const saveSpon = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...sponForm, sort: Number(sponForm.sort) || 0 };
      if (editing) await api.put(`/admin/sponsors/${editing}`, payload); else await api.post("/admin/sponsors", payload);
      toast.success("Saved"); setDialog(null); load();
    } catch (e2) { err(e2); }
  };
  const bulkAddPartners = async () => {
    const names = bulkText.split("\n").map((x) => x.trim()).filter(Boolean);
    if (names.length === 0) { toast.error("Add at least one name"); return; }
    const baseSort = 500;
    let ok = 0;
    for (let i = 0; i < names.length; i++) {
      try {
        await api.post("/admin/sponsors", {
          name: names[i], logo: "", link: "", tier: "Community Partner",
          blurb: "", sort: baseSort + i, dark_chip: false, compact: true,
        });
        ok += 1;
      } catch (e2) { /* skip and continue */ }
    }
    toast.success(`Added ${ok} community partner${ok === 1 ? "" : "s"}`);
    setBulkText(""); setBulkOpen(false); load();
  };

  const openArt = (a) => { setEditing(a?.id || null); setArtForm(a ? { ...emptyArticle, ...a } : emptyArticle); setDialog("article"); };
  const openStory = (s) => { setEditing(s?.id || null); setStoryForm(s ? { ...emptyStory, ...s, imagesText: (s.images || []).join("\n") } : emptyStory); setDialog("story"); };
  const openEvent = (e) => { setEditing(e?.id || null); setEventForm(e ? { ...emptyEvent, ...e, date: toLocalInput(e.date) } : emptyEvent); setDialog("event"); };
  const openCorr = (c) => { setEditing(c?.id || null); setCorrForm(c ? { ...emptyCorr, ...c } : emptyCorr); setDialog("corr"); };
  const openProd = (p) => { setEditing(p?.id || null); setProdForm(p ? { ...emptyProduct, ...p } : emptyProduct); setDialog("product"); };
  const openPotm = (p) => { setEditing(p?.id || null); setPotmForm(p ? { ...emptyPotm, ...p, interviewText: interviewToText(p.interview) } : emptyPotm); setDialog("potm"); };
  const openSpon = (s) => { setEditing(s?.id || null); setSponForm(s ? { ...emptySponsor, ...s } : emptySponsor); setDialog("sponsor"); };

  const fld = "mt-1.5 rounded-none border-ink";

  return (
    <div data-testid="admin-page" className="min-h-screen bg-paper">
      <header className="bg-ink text-white sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={MARK_URL} alt="The Canary Report" className="h-9 w-9" />
            <span className="font-display font-bold text-lg">Editor&rsquo;s Desk</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="label text-white/60 hover:text-canary flex items-center gap-1.5"><ExternalLink size={14} /> View site</Link>
            <span className="hidden sm:block label text-white/40">{user?.email}</span>
            <Button onClick={logout} data-testid="logout-btn" variant="ghost" className="text-white hover:text-canary hover:bg-white/5 label"><LogOut size={15} className="mr-1.5" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 sm:px-8 py-10">
        <Tabs defaultValue="articles">
          <TabsList className="bg-white border border-ink/15 rounded-none p-1 mb-8 flex-wrap h-auto">
            <TabsTrigger value="articles" data-testid="tab-articles" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Articles ({data.articles.length})</TabsTrigger>
            <TabsTrigger value="stories" data-testid="tab-stories" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Photos ({data.stories.length})</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Events ({data.events.length})</TabsTrigger>
            <TabsTrigger value="correspondents" data-testid="tab-correspondents" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Correspondents ({data.correspondents.length})</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Shop ({data.products.length})</TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="tab-sponsors" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Sponsors ({data.sponsors.length})</TabsTrigger>
            <TabsTrigger value="potm" data-testid="tab-potm" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Photo/Month ({data.potm.length})</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Inquiries ({data.inquiries.length})</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-none label data-[state=active]:bg-ink data-[state=active]:text-white">Messages ({data.messages.length})</TabsTrigger>
          </TabsList>

          {/* ARTICLES */}
          <TabsContent value="articles">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold">Articles</h2>
              <Button onClick={() => openArt(null)} data-testid="new-article-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label"><Plus size={16} className="mr-1.5" /> New article</Button>
            </div>
            <div className="grid gap-3">
              {data.articles.map((a) => (
                <Row key={a.id} testid={`admin-article-${a.id}`} img={a.cover_image}
                  meta={`${a.category}${a.featured ? " · Featured" : ""}${!a.published ? " · Draft" : ""}`}
                  title={a.title} onEdit={() => openArt(a)} onDelete={() => del(`/admin/articles/${a.id}`)} />
              ))}
              {data.articles.length === 0 && <p className="text-ink/50">No articles yet.</p>}
            </div>
          </TabsContent>

          {/* STORIES */}
          <TabsContent value="stories">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold">Photo Stories</h2>
              <Button onClick={() => openStory(null)} data-testid="new-story-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label"><Plus size={16} className="mr-1.5" /> New photo story</Button>
            </div>
            <div className="grid gap-3">
              {data.stories.map((s) => (
                <Row key={s.id} testid={`admin-story-${s.id}`} img={s.cover_image}
                  meta={`${s.location} · ${(s.images || []).length} photos`} title={s.title}
                  onEdit={() => openStory(s)} onDelete={() => del(`/admin/photostories/${s.id}`)} />
              ))}
              {data.stories.length === 0 && <p className="text-ink/50">No photo stories yet.</p>}
            </div>
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold">Events</h2>
              <Button onClick={() => openEvent(null)} data-testid="new-event-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label"><Plus size={16} className="mr-1.5" /> New event</Button>
            </div>
            <div className="grid gap-3">
              {data.events.map((e) => (
                <Row key={e.id} testid={`admin-event-${e.id}`} img={e.image}
                  meta={`${e.date ? new Date(e.date).toLocaleDateString() : "No date"} · ${e.location}`} title={e.title}
                  onEdit={() => openEvent(e)} onDelete={() => del(`/admin/events/${e.id}`)} />
              ))}
              {data.events.length === 0 && <p className="text-ink/50">No events yet.</p>}
            </div>
          </TabsContent>

          {/* CORRESPONDENTS */}
          <TabsContent value="correspondents">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold">Correspondents</h2>
              <Button onClick={() => openCorr(null)} data-testid="new-corr-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label"><Plus size={16} className="mr-1.5" /> New correspondent</Button>
            </div>
            <div className="grid gap-3">
              {data.correspondents.map((c) => (
                <Row key={c.id} testid={`admin-corr-${c.id}`} img={c.photo} meta={c.role} title={c.name}
                  onEdit={() => openCorr(c)} onDelete={() => del(`/admin/correspondents/${c.id}`)} />
              ))}
              {data.correspondents.length === 0 && <p className="text-ink/50">No correspondents yet.</p>}
            </div>
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold">Shop</h2>
              <Button onClick={() => openProd(null)} data-testid="new-product-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label"><Plus size={16} className="mr-1.5" /> New product</Button>
            </div>
            <div className="grid gap-3">
              {data.products.map((p) => (
                <Row key={p.id} testid={`admin-product-${p.id}`} img={p.image}
                  meta={`${p.category} · ${p.price}`} title={p.name}
                  onEdit={() => openProd(p)} onDelete={() => del(`/admin/products/${p.id}`)} />
              ))}
              {data.products.length === 0 && <p className="text-ink/50">No products yet.</p>}
            </div>
          </TabsContent>

          {/* SPONSORS */}
          <TabsContent value="sponsors">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold">Sponsors</h2>
              <div className="flex gap-2">
                <Button onClick={() => setBulkOpen((o) => !o)} data-testid="bulk-add-toggle" variant="outline" className="rounded-full border-ink label"><Plus size={16} className="mr-1.5" /> Bulk add community partners</Button>
                <Button onClick={() => openSpon(null)} data-testid="new-sponsor-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label"><Plus size={16} className="mr-1.5" /> New sponsor</Button>
              </div>
            </div>
            {bulkOpen && (
              <div data-testid="bulk-add-panel" className="bg-white border border-ink p-5 mb-6 space-y-3">
                <p className="label text-ink/60">Paste names of community partners (one per line). Each will be added as a compact, name-only entry under <em>Community Partners</em>.</p>
                <Textarea rows={6} value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="bulk-add-text" className="rounded-none border-ink font-mono text-sm" placeholder={"Windy Pines Foundation\nThe Denver Foundation\nSarah & James Smith\n…"} />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setBulkText(""); setBulkOpen(false); }} className="rounded-full border-ink label">Cancel</Button>
                  <Button onClick={bulkAddPartners} data-testid="bulk-add-save" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label">Add all</Button>
                </div>
              </div>
            )}
            <div className="grid gap-3">
              {data.sponsors.map((s) => (
                <Row key={s.id} testid={`admin-sponsor-${s.id}`} img={s.logo}
                  meta={`${s.tier}${s.compact ? " · Community (name only)" : ""}${s.link ? " · linked" : ""}`} title={s.name}
                  onEdit={() => openSpon(s)} onDelete={() => del(`/admin/sponsors/${s.id}`)} />
              ))}
              {data.sponsors.length === 0 && <p className="text-ink/50">No sponsors yet.</p>}
            </div>
          </TabsContent>

          {/* PHOTO OF THE MONTH */}
          <TabsContent value="potm">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold">Canary Photo of the Month</h2>
              <Button onClick={() => openPotm(null)} data-testid="new-potm-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label"><Plus size={16} className="mr-1.5" /> New feature</Button>
            </div>
            <div className="grid gap-3">
              {data.potm.map((p) => (
                <Row key={p.id} testid={`admin-potm-${p.id}`} img={p.photo}
                  meta={`${p.month}${p.featured ? " · Current" : ""} · ${(p.interview || []).length} Qs`} title={p.title}
                  onEdit={() => openPotm(p)} onDelete={() => del(`/admin/potm/${p.id}`)} />
              ))}
              {data.potm.length === 0 && <p className="text-ink/50">No features yet.</p>}
            </div>

            <div className="mt-12">
              <h3 className="font-display text-xl font-bold mb-4">Subscriber submissions ({data.potmSubs.length})</h3>
              <div className="grid gap-3">
                {data.potmSubs.map((sub) => (
                  <div key={sub.id} data-testid={`admin-potmsub-${sub.id}`} className="bg-white border border-ink/15 p-4 flex gap-4">
                    {sub.photo ? <img src={sub.photo} alt="" className="h-24 w-32 object-cover bg-paper flex-shrink-0" /> : <div className="h-24 w-32 bg-paper flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg font-bold">{sub.name}</p>
                      <p className="text-ink/70 text-sm"><a href={`mailto:${sub.email}`} className="link-underline">{sub.email}</a>{sub.location ? ` · ${sub.location}` : ""}</p>
                      <p className="text-ink/80 mt-2 leading-relaxed">{sub.story}</p>
                    </div>
                    <Button size="icon" variant="outline" onClick={() => del(`/admin/potm-submissions/${sub.id}`)} data-testid={`delete-potmsub-${sub.id}`} className={`${cellBtn} hover:bg-destructive hover:text-white self-start`}><Trash2 size={15} /></Button>
                  </div>
                ))}
                {data.potmSubs.length === 0 && <p className="text-ink/50">No submissions yet.</p>}
              </div>
            </div>
          </TabsContent>

          {/* INQUIRIES */}
          <TabsContent value="inquiries">
            <h2 className="font-display text-2xl font-bold mb-6">Partnership Inquiries</h2>
            <div className="grid gap-3">
              {data.inquiries.map((q) => (
                <div key={q.id} data-testid={`admin-inquiry-${q.id}`} className="bg-white border border-ink/15 p-5">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="label text-ink/50 mb-1">{q.partnership_type}</p>
                      <p className="font-display text-xl font-bold">{q.org_name}</p>
                      <p className="text-ink/70 text-sm mt-0.5">{q.contact_name} · <a href={`mailto:${q.email}`} className="link-underline">{q.email}</a>{q.phone ? ` · ${q.phone}` : ""}</p>
                    </div>
                    <Button size="icon" variant="outline" onClick={() => del(`/admin/inquiries/${q.id}`)} data-testid={`delete-inquiry-${q.id}`} className={`${cellBtn} hover:bg-destructive hover:text-white`}><Trash2 size={15} /></Button>
                  </div>
                  <p className="text-ink/80 mt-3 leading-relaxed">{q.message}</p>
                </div>
              ))}
              {data.inquiries.length === 0 && <p className="text-ink/50">No inquiries yet.</p>}
            </div>
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages">
            <h2 className="font-display text-2xl font-bold mb-6">Contact Messages</h2>
            <div className="grid gap-3">
              {data.messages.map((m) => (
                <div key={m.id} data-testid={`admin-message-${m.id}`} className="bg-white border border-ink/15 p-5">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="label text-ink/50 mb-1">{m.subject}</p>
                      <p className="font-display text-xl font-bold">{m.name}</p>
                      <p className="text-ink/70 text-sm mt-0.5"><a href={`mailto:${m.email}`} className="link-underline">{m.email}</a></p>
                    </div>
                    <Button size="icon" variant="outline" onClick={() => del(`/admin/messages/${m.id}`)} data-testid={`delete-message-${m.id}`} className={`${cellBtn} hover:bg-destructive hover:text-white`}><Trash2 size={15} /></Button>
                  </div>
                  <p className="text-ink/80 mt-3 leading-relaxed">{m.message}</p>
                </div>
              ))}
              {data.messages.length === 0 && <p className="text-ink/50">No messages yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* ARTICLE DIALOG */}
      <Dialog open={dialog === "article"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit article" : "New article"}</DialogTitle><DialogDescription className="sr-only">Article editor</DialogDescription></DialogHeader>
          <form onSubmit={saveArt} className="space-y-4" data-testid="article-form">
            <div><Label className="label">Title</Label><Input required value={artForm.title} onChange={(e) => setArtForm((f) => ({ ...f, title: e.target.value }))} data-testid="art-title" className={fld} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="label">Category</Label>
                <Select value={artForm.category} onValueChange={(v) => setArtForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className={fld} data-testid="art-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="label">Author</Label><Input value={artForm.author} onChange={(e) => setArtForm((f) => ({ ...f, author: e.target.value }))} data-testid="art-author" className={fld} /></div>
            </div>
            <div><Label className="label">Cover image URL</Label><Input value={artForm.cover_image} onChange={(e) => setArtForm((f) => ({ ...f, cover_image: e.target.value }))} data-testid="art-cover" className={fld} placeholder="https://…" /></div>
            <div><Label className="label">Excerpt</Label><Textarea rows={2} value={artForm.excerpt} onChange={(e) => setArtForm((f) => ({ ...f, excerpt: e.target.value }))} data-testid="art-excerpt" className={fld} /></div>
            <div><Label className="label">Body</Label><Textarea rows={6} value={artForm.body} onChange={(e) => setArtForm((f) => ({ ...f, body: e.target.value }))} data-testid="art-body" className={fld} /></div>
            <div className="flex gap-8">
              <label className="flex items-center gap-2.5 label"><Switch checked={artForm.featured} onCheckedChange={(v) => setArtForm((f) => ({ ...f, featured: v }))} data-testid="art-featured" /> Featured</label>
              <label className="flex items-center gap-2.5 label"><Switch checked={artForm.published} onCheckedChange={(v) => setArtForm((f) => ({ ...f, published: v }))} data-testid="art-published" /> Published</label>
            </div>
            <DialogFooter><Button type="submit" data-testid="save-article-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label px-8">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* STORY DIALOG */}
      <Dialog open={dialog === "story"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit photo story" : "New photo story"}</DialogTitle><DialogDescription className="sr-only">Photo story editor</DialogDescription></DialogHeader>
          <form onSubmit={saveStory} className="space-y-4" data-testid="story-form">
            <div><Label className="label">Title</Label><Input required value={storyForm.title} onChange={(e) => setStoryForm((f) => ({ ...f, title: e.target.value }))} data-testid="story-title" className={fld} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="label">Location</Label><Input value={storyForm.location} onChange={(e) => setStoryForm((f) => ({ ...f, location: e.target.value }))} data-testid="story-location" className={fld} /></div>
              <div><Label className="label">Photographer</Label><Input value={storyForm.photographer} onChange={(e) => setStoryForm((f) => ({ ...f, photographer: e.target.value }))} data-testid="story-photographer" className={fld} /></div>
            </div>
            <div><Label className="label">Description</Label><Textarea rows={2} value={storyForm.description} onChange={(e) => setStoryForm((f) => ({ ...f, description: e.target.value }))} data-testid="story-description" className={fld} /></div>
            <div>
              <Label className="label">Photos</Label>
              <label data-testid="story-upload" className="mt-1.5 cursor-pointer inline-flex items-center gap-2 label border border-ink px-4 py-2.5 hover:bg-ink hover:text-white transition-colors">
                <Plus size={14} /> Upload photos
                <input type="file" accept="image/*" multiple onChange={onStoryImages} className="hidden" />
              </label>
              <Textarea rows={5} value={storyForm.imagesText} onChange={(e) => setStoryForm((f) => ({ ...f, imagesText: e.target.value }))} data-testid="story-images" className={`${fld} font-mono text-xs`} placeholder={"Upload above, or paste image URLs (one per line)"} />
              <p className="text-ink/40 text-xs mt-1">Uploaded photos are added here automatically. You can also paste image URLs, one per line.</p>
            </div>
            <DialogFooter><Button type="submit" data-testid="save-story-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label px-8">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* EVENT DIALOG */}
      <Dialog open={dialog === "event"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit event" : "New event"}</DialogTitle><DialogDescription className="sr-only">Event editor</DialogDescription></DialogHeader>
          <form onSubmit={saveEvent} className="space-y-4" data-testid="event-form">
            <div><Label className="label">Title</Label><Input required value={eventForm.title} onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))} data-testid="event-title" className={fld} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="label">Date &amp; time</Label><Input type="datetime-local" value={eventForm.date} onChange={(e) => setEventForm((f) => ({ ...f, date: e.target.value }))} data-testid="event-date" className={fld} /></div>
              <div><Label className="label">Location</Label><Input value={eventForm.location} onChange={(e) => setEventForm((f) => ({ ...f, location: e.target.value }))} data-testid="event-location" className={fld} /></div>
            </div>
            <div><Label className="label">Image URL</Label><Input value={eventForm.image} onChange={(e) => setEventForm((f) => ({ ...f, image: e.target.value }))} data-testid="event-image" className={fld} placeholder="https://…" /></div>
            <div><Label className="label">RSVP / link</Label><Input value={eventForm.link} onChange={(e) => setEventForm((f) => ({ ...f, link: e.target.value }))} data-testid="event-link" className={fld} placeholder="https://…" /></div>
            <div><Label className="label">Description</Label><Textarea rows={3} value={eventForm.description} onChange={(e) => setEventForm((f) => ({ ...f, description: e.target.value }))} data-testid="event-description" className={fld} /></div>
            <DialogFooter><Button type="submit" data-testid="save-event-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label px-8">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CORRESPONDENT DIALOG */}
      <Dialog open={dialog === "corr"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit correspondent" : "New correspondent"}</DialogTitle><DialogDescription className="sr-only">Correspondent editor</DialogDescription></DialogHeader>
          <form onSubmit={saveCorr} className="space-y-4" data-testid="corr-form">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="label">Name</Label><Input required value={corrForm.name} onChange={(e) => setCorrForm((f) => ({ ...f, name: e.target.value }))} data-testid="corr-name" className={fld} /></div>
              <div><Label className="label">Role</Label><Input value={corrForm.role} onChange={(e) => setCorrForm((f) => ({ ...f, role: e.target.value }))} data-testid="corr-role" className={fld} /></div>
            </div>
            <div><Label className="label">Photo URL</Label><Input value={corrForm.photo} onChange={(e) => setCorrForm((f) => ({ ...f, photo: e.target.value }))} data-testid="corr-photo" className={fld} placeholder="https://…" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="label">Substack URL</Label><Input value={corrForm.substack} onChange={(e) => setCorrForm((f) => ({ ...f, substack: e.target.value }))} data-testid="corr-substack" className={fld} /></div>
              <div><Label className="label">LinkedIn URL</Label><Input value={corrForm.linkedin} onChange={(e) => setCorrForm((f) => ({ ...f, linkedin: e.target.value }))} data-testid="corr-linkedin" className={fld} /></div>
            </div>
            <div><Label className="label">Bio (paragraphs separated by blank lines)</Label><Textarea rows={6} value={corrForm.bio} onChange={(e) => setCorrForm((f) => ({ ...f, bio: e.target.value }))} data-testid="corr-bio" className={fld} /></div>
            <DialogFooter><Button type="submit" data-testid="save-corr-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label px-8">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PRODUCT DIALOG */}
      <Dialog open={dialog === "product"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit product" : "New product"}</DialogTitle><DialogDescription className="sr-only">Product editor</DialogDescription></DialogHeader>
          <form onSubmit={saveProd} className="space-y-4" data-testid="product-form">
            <div><Label className="label">Name</Label><Input required value={prodForm.name} onChange={(e) => setProdForm((f) => ({ ...f, name: e.target.value }))} data-testid="prod-name" className={fld} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="label">Category</Label>
                <Select value={prodForm.category} onValueChange={(v) => setProdForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className={fld} data-testid="prod-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRODUCT_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="label">Price</Label><Input value={prodForm.price} onChange={(e) => setProdForm((f) => ({ ...f, price: e.target.value }))} data-testid="prod-price" className={fld} placeholder="$18" /></div>
              <div><Label className="label">Sort</Label><Input type="number" value={prodForm.sort} onChange={(e) => setProdForm((f) => ({ ...f, sort: e.target.value }))} data-testid="prod-sort" className={fld} /></div>
            </div>
            <div><Label className="label">Image URL</Label><Input value={prodForm.image} onChange={(e) => setProdForm((f) => ({ ...f, image: e.target.value }))} data-testid="prod-image" className={fld} placeholder="https://…" /></div>
            <div><Label className="label">Store link</Label><Input value={prodForm.link} onChange={(e) => setProdForm((f) => ({ ...f, link: e.target.value }))} data-testid="prod-link" className={fld} placeholder="https://…" /></div>
            <div><Label className="label">Description</Label><Textarea rows={3} value={prodForm.description} onChange={(e) => setProdForm((f) => ({ ...f, description: e.target.value }))} data-testid="prod-description" className={fld} /></div>
            <DialogFooter><Button type="submit" data-testid="save-product-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label px-8">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SPONSOR DIALOG */}
      <Dialog open={dialog === "sponsor"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit sponsor" : "New sponsor"}</DialogTitle><DialogDescription className="sr-only">Sponsor editor</DialogDescription></DialogHeader>
          <form onSubmit={saveSpon} className="space-y-4" data-testid="sponsor-form">
            <div><Label className="label">Name</Label><Input required value={sponForm.name} onChange={(e) => setSponForm((f) => ({ ...f, name: e.target.value }))} data-testid="spon-name" className={fld} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="label">Tier</Label>
                <Select value={sponForm.tier} onValueChange={(v) => setSponForm((f) => ({ ...f, tier: v }))}>
                  <SelectTrigger className={fld} data-testid="spon-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>{SPONSOR_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="label">Sort</Label><Input type="number" value={sponForm.sort} onChange={(e) => setSponForm((f) => ({ ...f, sort: e.target.value }))} data-testid="spon-sort" className={fld} /></div>
            </div>
            <div><Label className="label">Website link</Label><Input value={sponForm.link} onChange={(e) => setSponForm((f) => ({ ...f, link: e.target.value }))} data-testid="spon-link" className={fld} placeholder="https://…" /></div>
            <div>
              <Label className="label">Logo</Label>
              <label data-testid="spon-upload" className="mt-1.5 cursor-pointer inline-flex items-center gap-2 label border border-ink px-4 py-2.5 hover:bg-ink hover:text-white transition-colors">
                <Plus size={14} /> Upload logo
                <input type="file" accept="image/*" onChange={onSponLogo} className="hidden" />
              </label>
              <Input value={sponForm.logo} onChange={(e) => setSponForm((f) => ({ ...f, logo: e.target.value }))} data-testid="spon-logo" className={`${fld} font-mono text-xs`} placeholder="Upload above, or paste a logo URL. Leave blank to show the name as text." />
              {sponForm.logo && <img src={sponForm.logo} alt="" className="mt-3 max-h-16 w-auto object-contain bg-paper p-2" />}
            </div>
            <div><Label className="label">Blurb (shown on the roster)</Label><Textarea rows={2} value={sponForm.blurb} onChange={(e) => setSponForm((f) => ({ ...f, blurb: e.target.value }))} data-testid="spon-blurb" className={fld} /></div>
            <label className="flex items-center gap-2.5 label"><Switch checked={!!sponForm.dark_chip} onCheckedChange={(v) => setSponForm((f) => ({ ...f, dark_chip: v }))} data-testid="spon-darkchip" /> Dark chip (use for white/light logos)</label>
            <label className="flex items-center gap-2.5 label"><Switch checked={!!sponForm.compact} onCheckedChange={(v) => setSponForm((f) => ({ ...f, compact: v }))} data-testid="spon-compact" /> Compact (name-only in Community Partners list)</label>
            <DialogFooter><Button type="submit" data-testid="save-sponsor-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label px-8">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PHOTO OF THE MONTH DIALOG */}
      <Dialog open={dialog === "potm"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit feature" : "New Photo of the Month"}</DialogTitle><DialogDescription className="sr-only">Photo of the Month editor</DialogDescription></DialogHeader>
          <form onSubmit={savePotm} className="space-y-4" data-testid="potm-form">
            <div><Label className="label">Photo title / headline</Label><Input required value={potmForm.title} onChange={(e) => setPotmForm((f) => ({ ...f, title: e.target.value }))} data-testid="potm-title" className={fld} /></div>
            <div><Label className="label">Photo URL</Label><Input value={potmForm.photo} onChange={(e) => setPotmForm((f) => ({ ...f, photo: e.target.value }))} data-testid="potm-photo-url" className={fld} placeholder="https://…" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="label">Photographer</Label><Input value={potmForm.photographer} onChange={(e) => setPotmForm((f) => ({ ...f, photographer: e.target.value }))} data-testid="potm-photographer" className={fld} placeholder="Subscriber name" /></div>
              <div><Label className="label">Location</Label><Input value={potmForm.location} onChange={(e) => setPotmForm((f) => ({ ...f, location: e.target.value }))} data-testid="potm-location" className={fld} /></div>
              <div><Label className="label">Month label</Label><Input value={potmForm.month} onChange={(e) => setPotmForm((f) => ({ ...f, month: e.target.value }))} data-testid="potm-month" className={fld} placeholder="January 2026" /></div>
            </div>
            <div><Label className="label">Intro</Label><Textarea rows={3} value={potmForm.intro} onChange={(e) => setPotmForm((f) => ({ ...f, intro: e.target.value }))} data-testid="potm-intro" className={fld} /></div>
            <div>
              <Label className="label">Interview (use Q: and A: — separate each pair with a blank line)</Label>
              <Textarea rows={8} value={potmForm.interviewText} onChange={(e) => setPotmForm((f) => ({ ...f, interviewText: e.target.value }))} data-testid="potm-interview" className={`${fld} text-sm`} placeholder={"Q: What drew you to this moment?\nA: I've walked this street my whole life…\n\nQ: What do you want people to feel?\nA: …"} />
            </div>
            <label className="flex items-center gap-2.5 label"><Switch checked={potmForm.featured} onCheckedChange={(v) => setPotmForm((f) => ({ ...f, featured: v }))} data-testid="potm-featured" /> Set as current feature</label>
            <DialogFooter><Button type="submit" data-testid="save-potm-btn" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink label px-8">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
